import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from '@/store';
import { useAuth } from '@/hooks';
import { OrganizationProvider, ERPConfigProvider } from '@/context';

// Pages
import DashboardPage from '@/pages/DashboardPage';
import LoginPage from '@/pages/LoginPage';
import RegistrationPage from '@/pages/RegistrationPage';
import CollaboratorsPage from '@/pages/CollaboratorsPage';
import ProductInventoryPage from '@/pages/ProductInventoryPage';
import ProductDetailPage from '@/pages/ProductDetailPage';
import SalesPage from '@/pages/SalesPage';
import CreateSalesPage from '@/pages/CreateSalesPage';
import BatchListPage from '@/pages/BatchListPage';
import PurchaseOrdersPage from '@/pages/PurchaseOrdersPage';
import PurchaseOrderDetailPage from '@/pages/PurchaseOrderDetailPage';
import GRNsPage from '@/pages/GRNsPage';
import GRNDetailPage from '@/pages/GRNDetailPage';
import WarehousesPage from '@/pages/WarehousesPage';
import WarehouseDetailPage from '@/pages/WarehouseDetailPage';
import DiscountsPage from '@/pages/DiscountsPage';
import ReturnsPage from '@/pages/ReturnsPage';
import ReturnDetailPage from '@/pages/ReturnDetailPage';
import ProductPricesPage from '@/pages/ProductPricesPage';
import CategoriesPage from '@/pages/CategoriesPage';
import SubcategoriesPage from '@/pages/SubcategoriesPage';
import ReportsPage from '@/pages/ReportsPage';
import {
  ProductsReportPage,
  VendorsReportPage,
  CustomersReportPage,
  InventoryReportPage,
  PurchasesReportPage,
  SalesReportPage,
  ReturnsReportPage,
} from '@/pages/reports';
import FPOSettingsPage from '@/pages/FPOSettingsPage';
import FPOProfilePage from '@/pages/FPOProfilePage';
import NotFoundPage from '@/pages/NotFoundPage';

// Layout component
import MainLayout from '@/components/organisms/MainLayout';

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegistrationPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<MainLayout />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="collaborators" element={<CollaboratorsPage />} />
        <Route path="product-inventory" element={<ProductInventoryPage />} />
        <Route path="product-inventory/:id" element={<ProductDetailPage />} />
        {/* Backwards compatibility redirects */}
        <Route path="products" element={<Navigate to="/product-inventory" replace />} />
        <Route path="products/:id" element={<ProductDetailPage />} />
        <Route path="inventory" element={<Navigate to="/product-inventory" replace />} />
        <Route path="batches" element={<BatchListPage />} />
        <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
        <Route path="purchase-orders/:id" element={<PurchaseOrderDetailPage />} />
        <Route path="grns" element={<GRNsPage />} />
        <Route path="grns/:id" element={<GRNDetailPage />} />
        <Route path="warehouses" element={<WarehousesPage />} />
        <Route path="warehouses/:id" element={<WarehouseDetailPage />} />
        <Route path="sales" element={<SalesPage />} />
        <Route path="sales/create" element={<CreateSalesPage />} />
        <Route path="returns" element={<ReturnsPage />} />
        <Route path="returns/:id" element={<ReturnDetailPage />} />
        <Route path="discounts" element={<DiscountsPage />} />
        <Route path="products/prices" element={<ProductPricesPage />} />
        <Route path="categories" element={<CategoriesPage />} />
        <Route path="subcategories" element={<SubcategoriesPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="reports/products" element={<ProductsReportPage />} />
        <Route path="reports/vendors" element={<VendorsReportPage />} />
        <Route path="reports/customers" element={<CustomersReportPage />} />
        <Route path="reports/inventory" element={<InventoryReportPage />} />
        <Route path="reports/purchases" element={<PurchasesReportPage />} />
        <Route path="reports/sales" element={<SalesReportPage />} />
        <Route path="reports/returns" element={<ReturnsReportPage />} />
        <Route path="settings" element={<FPOSettingsPage />} />
        <Route path="profile" element={<FPOProfilePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <OrganizationProvider>
          <ERPConfigProvider>
            <Router>
              <AppRoutes />
            </Router>
          </ERPConfigProvider>
        </OrganizationProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
